import { SetStateAction, useState, useRef, useMemo } from "react";
import { Button, Tooltip } from "@fluentui/react-components";
import { Mic28Filled } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import styles from "./QuestionInput.module.css";
import { supportedLngs } from "../../i18n/config";

interface Props {
    updateQuestion: (question: string) => void;
    question: string;
}

const useCustomSpeechRecognition = () => {
    const { i18n } = useTranslation();
    const currentLng = i18n.language;
    
    return useMemo(() => {
        let lngCode = supportedLngs[currentLng]?.locale;
        if (!lngCode) {
            lngCode = "en-US";
        }

        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) return null;

        try {
            const recognition = new SpeechRecognition();
            recognition.lang = lngCode;
            recognition.interimResults = true;
            recognition.continuous = true; // Keep recording even if user pauses
            return recognition;
        } catch (err) {
            console.error("SpeechRecognition not supported", err);
            return null;
        }
    }, [currentLng]);
};

export const SpeechInput = ({ updateQuestion, question }: Props) => {
    const speechRecognition = useCustomSpeechRecognition();
    const { t } = useTranslation();
    const [isRecording, setIsRecording] = useState<boolean>(false);
    
    // Store the initial question text when recording starts
    const initialQuestionRef = useRef<string>("");

    const startRecording = () => {
        if (speechRecognition == null) {
            console.error("SpeechRecognition not supported");
            return;
        }

        // Save current text so we can append to it
        initialQuestionRef.current = question;

        speechRecognition.onresult = (event: { results: { transcript: SetStateAction<string> }[][] }) => {
            let input = "";
            for (const result of event.results) {
                input += result[0].transcript;
            }
            
            // Append new speech to the initial text
            // Add a space if there was already text
            const prefix = initialQuestionRef.current ? initialQuestionRef.current + " " : "";
            updateQuestion(prefix + input);
        };
        speechRecognition.onend = () => {
            // NOTE: In some browsers (e.g. Chrome), the recording will stop automatically after a few seconds of silence.
            setIsRecording(false);
        };
        speechRecognition.onerror = (event: { error: string }) => {
            if (speechRecognition) {
                // Ignore 'aborted' error which happens when stopping manually
                if (event.error === 'aborted') {
                    return;
                }
                
                speechRecognition.stop();
                if (event.error == "no-speech") {
                    alert("No speech was detected. Please check your system audio settings and try again.");
                } else if (event.error == "language-not-supported") {
                    alert(
                        `Speech recognition error detected: ${event.error}. The speech recognition input functionality does not yet work on all browsers, like Edge in Mac OS X with ARM chips. Try another browser/OS.`
                    );
                } else {
                    alert(`Speech recognition error detected: ${event.error}.`);
                }
            }
        };

        try {
            speechRecognition.start();
            setIsRecording(true);
        } catch (e) {
            console.error("Failed to start recording", e);
        }
    };

    const stopRecording = () => {
        if (speechRecognition == null) {
            console.error("SpeechRecognition not supported");
            return;
        }
        speechRecognition.stop();
        // Do not set isRecording(false) here, wait for onend event
        // This prevents race conditions where user clicks start before stop is complete
    };

    if (speechRecognition == null) {
        return <></>;
    }
    return (
        <>
            {!isRecording && (
                <div className={styles.questionInputButtonsContainer}>
                    <Tooltip content={t("tooltips.askWithVoice")} relationship="label">
                        <Button size="large" icon={<Mic28Filled primaryFill="rgba(0, 152, 53, 1)" />} onClick={startRecording} />
                    </Tooltip>
                </div>
            )}
            {isRecording && (
                <div className={styles.questionInputButtonsContainer}>
                    <Tooltip content={t("tooltips.stopRecording")} relationship="label">
                        <Button size="large" icon={<Mic28Filled primaryFill="rgba(250, 0, 0, 0.7)" />} disabled={!isRecording} onClick={stopRecording} />
                    </Tooltip>
                </div>
            )}
        </>
    );
};
